import React, { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faSearch,
    faFilter,
    faEdit,
    faTrash,
    faCheckCircle,
    faTimesCircle,
    faEye
} from '@fortawesome/free-solid-svg-icons';
import { get } from '../../../helpers/apiHelper';

const UsersList = ({ onEdit, onView, onDelete }) => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [filters, setFilters] = useState({
        role: '',
        plan: '',
        pais: '',
        isEmailVerified: '',
        hasPaid: '',
        validated: '',
        pagoPlan: ''
    });
    const [pagination, setPagination] = useState({
        currentPage: 1,
        totalPages: 1,
        totalUsers: 0
    });
    const [advancedFiltersVisible, setAdvancedFiltersVisible] = useState(false);

    const fetchUsers = useCallback(async () => {
        try {
            setLoading(true);

            // Construir la URL con los filtros y paginación
            const queryParams = new URLSearchParams({
                page: pagination.currentPage,
                ...Object.fromEntries(
                    Object.entries(filters).filter(([_, value]) => value !== '')
                )
            });

            if (searchTerm) {
                queryParams.append('search', searchTerm);
            }

            const url = `users${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;

            const response = await get(url);

            if (Array.isArray(response)) {
                setUsers(response);
                setPagination(prev => ({
                    ...prev,
                    totalUsers: response.length
                }));
            } else {
                setUsers([]);
            }
        } catch (error) {
            console.error('Error fetching users:', error);
            setUsers([]);
        } finally {
            setLoading(false);
        }
    }, [filters, pagination.currentPage, searchTerm]);

    useEffect(() => {
        fetchUsers();
    }, [fetchUsers]);

    const handleSearch = useCallback((e) => {
        setSearchTerm(e.target.value);
        // Implementar debounce aquí si es necesario
    }, []);

    const handleFilterChange = useCallback((filterName, value) => {
        setFilters(prev => ({
            ...prev,
            [filterName]: value
        }));
        setPagination(prev => ({ ...prev, currentPage: 1 })); // Reset page when filter changes
    }, []);

    const formatDate = useCallback((date) => {
        if (!date) return 'N/A';
        return new Date(date).toLocaleDateString('es-ES', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    }, []);

    const handleResendVerification = async (email) => {
        try {
            const button = document.querySelector(`button[data-email="${email}"]`);
            if (button) {
                button.disabled = true;
                button.innerHTML = '<span class="animate-spin">↻</span> Enviando...';
            }

            // await post('auth/resend-verification', { email }); // Eliminado por razones de seguridad

            // Mostrar mensaje de éxito
            alert('Email de verificación reenviado con éxito');
        } catch (error) {
            console.error('Error resending verification email:', error);
            alert('Error al reenviar el email de verificación: ' + (error.message || 'Error desconocido'));
        } finally {
            // Restaurar el botón
            const button = document.querySelector(`button[data-email="${email}"]`);
            if (button) {
                button.disabled = false;
                button.innerHTML = '<svg class="w-4 h-4 mr-1" ... /></svg> Reenviar email';
            }
        }
    };

    return (
        <div className="bg-white shadow-sm rounded-lg p-2 sm:p-6">
            {/* Barra de búsqueda y filtros básicos */}
            <div className="flex flex-col gap-4 mb-4 sm:mb-6">
                <div className="flex flex-col sm:flex-row gap-4">
                    <div className="flex-1 relative">
                        <input
                            type="text"
                            placeholder="Buscar por nombre, email o ID..."
                            className="w-full pl-10 pr-4 py-2 border rounded-md text-sm"
                            value={searchTerm}
                            onChange={handleSearch}
                        />
                        <FontAwesomeIcon
                            icon={faSearch}
                            className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                        />
                    </div>

                    <button
                        onClick={() => setAdvancedFiltersVisible(!advancedFiltersVisible)}
                        className="w-full sm:w-auto px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 flex items-center justify-center gap-2 text-sm"
                    >
                        <FontAwesomeIcon icon={faFilter} />
                        {advancedFiltersVisible ? 'Ocultar filtros' : 'Mostrar filtros'}
                    </button>
                </div>

                {/* Filtros avanzados */}
                {advancedFiltersVisible && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 p-2 sm:p-4 bg-gray-50 rounded-md">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">País</label>
                            <select
                                className="w-full border rounded-md px-3 py-2"
                                value={filters.pais}
                                onChange={(e) => handleFilterChange('pais', e.target.value)}
                            >
                                <option value="">Todos los países</option>
                                <option value="España">España</option>
                                <option value="Chile">Chile</option>
                                <option value="Perú">Perú</option>
                            </select>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Rol</label>
                            <select
                                className="w-full border rounded-md px-3 py-2"
                                value={filters.role}
                                onChange={(e) => handleFilterChange('role', e.target.value)}
                            >
                                <option value="">Todos los roles</option>
                                <option value="ADMIN_ROLE">Administrador</option>
                                <option value="USER_ROLE">Usuario</option>
                            </select>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Plan</label>
                            <select
                                className="w-full border rounded-md px-3 py-2"
                                value={filters.plan}
                                onChange={(e) => handleFilterChange('plan', e.target.value)}
                            >
                                <option value="">Todos los planes</option>
                                <option value="trial">Trial</option>
                                <option value="basic">Básico</option>
                                <option value="professional">Profesional</option>
                                <option value="enterprise">Empresa</option>
                            </select>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de pago</label>
                            <select
                                className="w-full border rounded-md px-3 py-2"
                                value={filters.pagoPlan}
                                onChange={(e) => handleFilterChange('pagoPlan', e.target.value)}
                            >
                                <option value="">Todos</option>
                                <option value="monthly">Mensual</option>
                                <option value="annual">Anual</option>
                            </select>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Estado de pago</label>
                            <select
                                className="w-full border rounded-md px-3 py-2"
                                value={filters.hasPaid}
                                onChange={(e) => handleFilterChange('hasPaid', e.target.value)}
                            >
                                <option value="">Todos</option>
                                <option value="true">Pagado</option>
                                <option value="false">Pendiente</option>
                            </select>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Email verificado</label>
                            <select
                                className="w-full border rounded-md px-3 py-2"
                                value={filters.isEmailVerified}
                                onChange={(e) => handleFilterChange('isEmailVerified', e.target.value)}
                            >
                                <option value="">Todos</option>
                                <option value="true">Verificado</option>
                                <option value="false">No verificado</option>
                            </select>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Estado</label>
                            <select
                                className="w-full border rounded-md px-3 py-2"
                                value={filters.validated}
                                onChange={(e) => handleFilterChange('validated', e.target.value)}
                            >
                                <option value="">Todos</option>
                                <option value="true">Activo</option>
                                <option value="false">Inactivo</option>
                            </select>
                        </div>
                    </div>
                )}
            </div>

            {/* Vista móvil de la tabla */}
            <div className="block sm:hidden">
                {loading ? (
                    <div className="text-center py-4">
                        <div className="flex justify-center items-center space-x-2">
                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                            <span>Cargando usuarios...</span>
                        </div>
                    </div>
                ) : users.length === 0 ? (
                    <div className="text-center py-4">
                        No se encontraron usuarios
                    </div>
                ) : (
                    <div className="space-y-4">
                        {users.map((user) => (
                            <div key={user._id} className="bg-white border rounded-lg p-4 shadow-sm">
                                <div className="flex items-center justify-between mb-4">
                                    <div className="flex items-center space-x-3">
                                        <div className="flex-shrink-0 h-10 w-10 bg-gray-100 rounded-full flex items-center justify-center">
                                            <span className="text-lg text-gray-600">
                                                {user.nombre.charAt(0).toUpperCase()}
                                            </span>
                                        </div>
                                        <div>
                                            <div className="font-medium text-gray-900">
                                                {user.nombre} {user.apellido}
                                            </div>
                                            <div className="text-sm text-gray-500">{user.email}</div>
                                        </div>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                        <button
                                            className="p-2 text-blue-600 hover:text-blue-900"
                                            onClick={() => onView(user)}
                                        >
                                            <FontAwesomeIcon icon={faEye} />
                                        </button>
                                        <button
                                            className="p-2 text-indigo-600 hover:text-indigo-900"
                                            onClick={() => onEdit(user)}
                                        >
                                            <FontAwesomeIcon icon={faEdit} />
                                        </button>
                                        <button
                                            className="p-2 text-red-600 hover:text-red-900"
                                            onClick={() => onDelete(user._id)}
                                        >
                                            <FontAwesomeIcon icon={faTrash} />
                                        </button>
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 gap-2 text-sm">
                                    <div>
                                        <span className="text-gray-500">País:</span>
                                        <span className="ml-1">{user.pais}</span>
                                    </div>
                                    <div>
                                        <span className="text-gray-500">Plan:</span>
                                        <span className={`ml-1 px-2 py-0.5 rounded-full text-xs ${{
                                                'trial': 'bg-yellow-100 text-yellow-800',
                                                'basic': 'bg-green-100 text-green-800',
                                                'professional': 'bg-blue-100 text-blue-800',
                                                'enterprise': 'bg-purple-100 text-purple-800'
                                            }[user.plan] || 'bg-gray-100 text-gray-800'
                                            }`}>
                                            {user.plan.charAt(0).toUpperCase() + user.plan.slice(1)}
                                        </span>
                                    </div>
                                    <div>
                                        <span className={`px-2 py-0.5 rounded-full text-xs ${user.isEmailVerified
                                                ? 'bg-green-100 text-green-800'
                                                : 'bg-yellow-100 text-yellow-800'
                                            }`}>
                                            {user.isEmailVerified ? 'Email verificado' : 'Email pendiente'}
                                        </span>
                                    </div>
                                    <div>
                                        <span className={`px-2 py-0.5 rounded-full text-xs ${user.hasPaid
                                                ? 'bg-green-100 text-green-800'
                                                : 'bg-red-100 text-red-800'
                                            }`}>
                                            {user.hasPaid ? 'Pago verificado' : 'Pago pendiente'}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Vista desktop de la tabla */}
            <div className="hidden sm:block overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Usuario
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Email
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                País
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Plan
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Estados
                            </th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Acciones
                            </th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {loading ? (
                            <tr>
                                <td colSpan="6" className="text-center py-4">
                                    <div className="flex justify-center items-center space-x-2">
                                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                                        <span>Cargando usuarios...</span>
                                    </div>
                                </td>
                            </tr>
                        ) : users.length === 0 ? (
                            <tr>
                                <td colSpan="6" className="text-center py-4">
                                    No se encontraron usuarios
                                </td>
                            </tr>
                        ) : (
                            users.map((user) => (
                                <tr key={user._id} className="hover:bg-gray-50">
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="flex items-center">
                                            <div className="flex-shrink-0 h-10 w-10 bg-gray-100 rounded-full flex items-center justify-center">
                                                <span className="text-lg text-gray-600">
                                                    {user.nombre.charAt(0).toUpperCase()}
                                                </span>
                                            </div>
                                            <div className="ml-4">
                                                <div className="text-sm font-medium text-gray-900">
                                                    {user.nombre} {user.apellido} {user.segundoapellido || ''}
                                                </div>
                                                <div className="text-xs text-gray-500 flex items-center gap-2">
                                                    <span className="font-mono">ID: {user._id.slice(-6)}</span>
                                                    <span className={`px-2 py-0.5 rounded-full text-xs ${user.role === 'ADMIN_ROLE'
                                                        ? 'bg-purple-100 text-purple-800'
                                                        : 'bg-blue-100 text-blue-800'
                                                        }`}>
                                                        {user.role === 'ADMIN_ROLE' ? 'Admin' : 'Usuario'}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="text-sm text-gray-900">{user.email}</div>
                                        <div className="text-xs text-gray-500 flex items-center gap-2">
                                            {/* Removed faGlobe as it's not imported */}
                                            {user.pais}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="text-sm text-gray-900">{user.email}</div>
                                        <div className="text-xs text-gray-500 flex items-center gap-2">
                                            {/* Removed faGlobe as it's not imported */}
                                            {user.pais}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="flex items-center gap-2">
                                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${{
                                                'trial': 'bg-yellow-100 text-yellow-800',
                                                'basic': 'bg-green-100 text-green-800',
                                                'professional': 'bg-blue-100 text-blue-800',
                                                'enterprise': 'bg-purple-100 text-purple-800'
                                            }[user.plan] || 'bg-gray-100 text-gray-800'
                                                }`}>
                                                {user.plan.charAt(0).toUpperCase() + user.plan.slice(1)}
                                            </span>
                                            {user.pagoPlan && (
                                                <span className="text-xs text-gray-500">
                                                    ({user.pagoPlan === 'monthly' ? 'Mensual' : 'Anual'})
                                                </span>
                                            )}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="flex flex-col gap-2">
                                            {/* Estado de Email */}
                                            <span className={`px-2 py-1 rounded-full text-xs font-medium flex items-center ${user.isEmailVerified
                                                ? 'bg-green-100 text-green-800'
                                                : 'bg-yellow-100 text-yellow-800'
                                                }`}>
                                                <FontAwesomeIcon
                                                    icon={user.isEmailVerified ? faCheckCircle : faTimesCircle}
                                                    className="mr-1"
                                                />
                                                {user.isEmailVerified ? 'Email verificado' : 'Email pendiente'}
                                            </span>

                                            {/* Estado de Pago */}
                                            <span className={`px-2 py-1 rounded-full text-xs font-medium flex items-center ${user.hasPaid
                                                ? 'bg-green-100 text-green-800'
                                                : 'bg-red-100 text-red-800'
                                                }`}>
                                                <FontAwesomeIcon
                                                    icon={user.hasPaid ? faCheckCircle : faTimesCircle}
                                                    className="mr-1"
                                                />
                                                {user.hasPaid ? 'Pago verificado' : 'Pago pendiente'}
                                            </span>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                        <button
                                            className="text-blue-600 hover:text-blue-900 mr-3"
                                            onClick={() => onView(user)}
                                            title="Ver detalles"
                                        >
                                            <FontAwesomeIcon icon={faEye} />
                                        </button>
                                        <button
                                            className="text-indigo-600 hover:text-indigo-900 mr-3"
                                            onClick={() => onEdit(user)}
                                            title="Editar usuario"
                                        >
                                            <FontAwesomeIcon icon={faEdit} />
                                        </button>
                                        <button
                                            className="text-red-600 hover:text-red-900"
                                            onClick={() => onDelete(user._id)}
                                            title="Eliminar usuario"
                                        >
                                            <FontAwesomeIcon icon={faTrash} />
                                        </button>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* Paginación responsive */}
            {!loading && users.length > 0 && (
                <div className="flex flex-col sm:flex-row justify-between items-center gap-4 mt-6 px-2 sm:px-0">
                    <div className="text-sm text-gray-700 text-center sm:text-left">
                        Mostrando <span className="font-medium">{users.length}</span> de{' '}
                        <span className="font-medium">{pagination.totalUsers}</span> usuarios
                    </div>
                    <div className="flex gap-2 w-full sm:w-auto">
                        <button
                            className="flex-1 sm:flex-none px-3 py-2 border rounded-md disabled:opacity-50 text-sm"
                            disabled={pagination.currentPage === 1}
                            onClick={() => setPagination(prev => ({ ...prev, currentPage: prev.currentPage - 1 }))}
                        >
                            Anterior
                        </button>
                        <button
                            className="flex-1 sm:flex-none px-3 py-2 border rounded-md disabled:opacity-50 text-sm"
                            disabled={pagination.currentPage === pagination.totalPages}
                            onClick={() => setPagination(prev => ({ ...prev, currentPage: prev.currentPage + 1 }))}
                        >
                            Siguiente
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default UsersList; 